import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
    DialogTitle, Grid, IconButton, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TrafficLightTable from './TrafficLightTable.jsx';
import TrafficLightPresets from './TrafficLightPresets.jsx';
import { formatDurationHuman } from './duration.mjs';
import { runtimeState, startOverride } from './presetState.mjs';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ScheduleIcon from '@mui/icons-material/Schedule';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TrafficLightTimings from './TrafficLightTimings.jsx';
import ScheduleDrawer from '../zone/ScheduleDrawer.jsx';
import ScheduleSummary from '../zone/ScheduleSummary.jsx';
import { COLORS } from './trafficLightState.mjs';

const t = (text) => window.__ ? window.__(text) : text;
const labels = { red: 'Red', amber: 'Amber', green: 'Green', off: 'Off', flash: 'Flashing amber', normal: 'Normal', night: 'Night' };
const lampColors = { red: '#ef4444', amber: '#fbbf24', green: '#22c55e' };
const EMPTY_SCHEDULE = [];

function TrafficLightPanel({ light, onSaveTimings, onConfigureRelays, presetRequest, onRuntimeChange }) {
    const [mode, setMode] = useState('automatic');
    const [manual, setManual] = useState('off');
    const [override, setOverride] = useState(null);
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const timer = setInterval(() => setNow(new Date()), 100);
        return () => clearInterval(timer);
    }, []);
    useEffect(() => {
        if (!presetRequest || presetRequest.lightId !== light.id) return;
        if (presetRequest.kind === 'schedule') { setMode('automatic'); setOverride(null); return; }
        const date = new Date();
        const current = runtimeState(light, mode, manual, override, date);
        setOverride(startOverride(presetRequest.preset, presetRequest.seconds, current, date.getTime()));
        setMode('automatic');
    }, [presetRequest]);
    const state = runtimeState(light, mode, manual, override, now);
    const displayLabel = labels[state.label] || state.label;
    useEffect(() => { onRuntimeChange({ mode, presetId: state.preset?.id || null, temporary: Boolean(state.temporary) }); }, [mode, state.preset?.id, Boolean(state.temporary), onRuntimeChange]);
    return <Grid container spacing={3} alignItems="center">
        <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, bgcolor: 'grey.100', borderRadius: 3, p: 3 }}>
                <Box role="img" aria-label={t(displayLabel)}
                    sx={{ bgcolor: '#172330', borderRadius: 5, p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {COLORS.map((color) => <Box key={color} sx={{ width: 62, height: 62, borderRadius: '50%', bgcolor: lampColors[color], opacity: state.color === color ? 1 : 0.15, boxShadow: state.color === color ? `0 0 20px ${lampColors[color]}66` : 'inset 0 2px 8px #0008' }} />)}
                </Box>
                <Typography fontWeight={700}>{t(displayLabel)}</Typography>
                <Typography sx={{ minHeight: 28, fontVariantNumeric: 'tabular-nums' }}>{state.remaining !== null ? formatDurationHuman(state.remaining) : '\u00a0'}</Typography>
            </Box>
        </Grid>
        <Grid item xs={12} sm={8}><Stack spacing={2}>
            <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, value) => {
                if (!value) return;
                if (value === 'manual') { setManual('off'); setOverride(null); }
                setMode(value);
            }} aria-label={t('Operating mode')}>
                <ToggleButton value="manual">{t('Manual')}</ToggleButton>
                <ToggleButton value="automatic">{t('Automatic')}</ToggleButton>
            </ToggleButtonGroup>
            {mode === 'manual' ? <Stack direction="row" gap={1} flexWrap="wrap">
                {[...COLORS, 'flash', 'off'].map((color) => <Button key={color} size="small" variant={manual === color ? 'contained' : 'outlined'} onClick={() => setManual(color)}>{t(labels[color])}</Button>)}
            </Stack> : <Box><Chip size="small" label={`${t('Active preset')}: ${state.preset?.name || 'Normal'}`} /></Box>}
            {state.temporary && <Alert severity="info" action={<Button color="inherit" onClick={() => setOverride({ preset: { ...override.preset, mode: 'hold', color: state.color }, start: Date.now(), end: Date.now() })}>{t('Return to schedule')}</Button>}>
                {state.returning ? t('Returning to schedule') : state.transitioning ? t('Amber transition') : state.preset.name} · {formatDurationHuman(Math.max(0, Math.ceil(((state.until || override.end) - now.getTime()) / 1000)))} {t('remaining')}
            </Alert>}
            <Typography variant="subtitle2">{t('Normal timings')}</Typography>
            <TrafficLightTimings light={light} onSave={onSaveTimings} />
            {light.device_edit_url ? <Button size="small" endIcon={<OpenInNewIcon />} onClick={onConfigureRelays}>{t('Configure relays on device')}</Button> : <Typography variant="body2" color="text.secondary">{t('Assign this traffic light from Device Management to configure its relays.')}</Typography>}
        </Stack></Grid>
    </Grid>;
}

export default function TrafficLightManagementApp({ initialList, trafficLight = null, listUrl, endpoints, csrfToken }) {
    const isDetail = Boolean(trafficLight);
    const [lights, setLights] = useState(() => trafficLight ? [trafficLight] : []);
    const [refreshKey, setRefreshKey] = useState(0);
    const [savedLight, setSavedLight] = useState(null);
    const [presetRequest, setPresetRequest] = useState(null);
    const [runtime, setRuntime] = useState({ mode: 'automatic', presetId: null, temporary: false });
    const [form, setForm] = useState(null);
    const [scheduleEditor, setScheduleEditor] = useState(null);
    const [scheduleError, setScheduleError] = useState('');
    const [scheduleSaving, setScheduleSaving] = useState(false);
    const [deviceNavigation, setDeviceNavigation] = useState(null);
    const scheduleLight = lights.find((light) => light.id === scheduleEditor?.lightId);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(null);
    async function request(url, method, body) {
        const response = await fetch(url, { method, credentials: 'same-origin', headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken }, ...(body ? { body: JSON.stringify(body) } : {}) });
        const data = await response.json();
        if (!response.ok) throw new Error(Object.values(data.errors || {}).flat().join(' ') || data.message || t('Unable to save changes.'));
        return data;
    }
    const edit = (light = {}) => {
        setError('');
        setForm({ id: light.id || null, name: light.name || '', description: light.description || '' });
    };
    const save = async () => {
        setSaving(true); setError('');
        try {
            const { data } = await request(form.id ? endpoints.update.replace('__ID__', form.id) : endpoints.create, form.id ? 'PUT' : 'POST', form);
            setLights((current) => (form.id ? current.map((light) => light.id === data.id ? data : light) : [...current, data]).sort((a, b) => a.name.localeCompare(b.name)));
            setForm(null); setSuccess(t('Traffic light saved.'));
            setSavedLight(data); setRefreshKey((key) => key + 1);
        } catch (failure) { setError(failure.message); }
        finally { setSaving(false); }
    };
    const updateLight = (data) => setLights((current) => current.map((light) => light.id === data.id ? data : light));
    const saveTimings = async (light, timings) => {
        const { data } = await request(endpoints.update.replace('__ID__', light.id), 'PUT', { timings });
        updateLight(data);
    };
    const saveSchedule = async (rows) => {
        setScheduleSaving(true); setScheduleError('');
        try {
            const { data } = await request(endpoints.update.replace('__ID__', scheduleLight.id), 'PUT', {
                schedule: { normal: rows },
            });
            updateLight(data); setSuccess(t('Schedule saved.'));
        } catch (failure) {
            setScheduleError(failure.message);
            return false;
        } finally { setScheduleSaving(false); }
    };
    const remove = async () => {
        setSaving(true); setError('');
        try {
            await request(endpoints.delete.replace('__ID__', deleting.id), 'DELETE');
            setLights((current) => current.filter((light) => light.id !== deleting.id));
            setDeleting(null); setSavedLight(null); setSuccess(t('Traffic light deleted.'));
            setRefreshKey((key) => key + 1);
        } catch (failure) { setError(failure.message); }
        finally { setSaving(false); }
    };
    return <Stack spacing={3}>
        {isDetail ? <Box><Button component="a" href={listUrl || endpoints.index} startIcon={<ArrowBackIcon />}>{t('Back to traffic lights')}</Button></Box>
            : <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}><Button startIcon={<AddIcon />} variant="contained" onClick={() => edit()}>{t('Add traffic light')}</Button></Box>}
        {success && <Alert severity="success" onClose={() => setSuccess('')}>{success}
            {!isDetail && savedLight?.detail_url && <Button component="a" href={savedLight.detail_url} size="small" sx={{ ml: 1 }}>{t('Open details')}</Button>}
        </Alert>}
        {error && !form && !deleting && <Alert severity="error">{error}</Alert>}
        {!isDetail && <TrafficLightTable initialList={initialList} endpoint={endpoints.list} refreshKey={refreshKey}
            onEdit={edit} onDelete={(light) => { setError(''); setDeleting(light); }} />}
        {isDetail && <Grid container spacing={3} sx={{ width: 'auto' }}>
            {lights.map((light) => <Grid item xs={12} key={light.id}><Card variant="outlined"><CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Box><Typography variant="h6">{light.name}</Typography><Typography variant="body2" color="text.secondary">{light.device_name || t('Unassigned')}</Typography></Box>
                    <Stack direction="row"><IconButton aria-label={`${t('Edit')} ${light.name}`} onClick={() => edit(light)}><EditIcon /></IconButton><IconButton aria-label={`${t('Delete')} ${light.name}`} onClick={() => { setError(''); setDeleting(light); }}><DeleteIcon /></IconButton></Stack>
                </Stack>
                {light.description && <Typography variant="body2" sx={{ mb: 2 }}>{light.description}</Typography>}
                <TrafficLightPanel light={light} presetRequest={presetRequest} onRuntimeChange={setRuntime} onSaveTimings={(timings) => saveTimings(light, timings)} onConfigureRelays={() => setDeviceNavigation(light)} />
                <Stack spacing={2} sx={{ mt: 3 }}>
                    {['normal', 'night'].map((profile) => <Box key={profile}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="subtitle2">{t(labels[profile])}{profile === 'night' ? ` · ${t('Flashing amber')}` : ''}</Typography>
                            {profile === 'normal' && <Button size="small" startIcon={<ScheduleIcon />} aria-label={`${t('Edit schedule')} ${t(labels[profile])} · ${light.name}`}
                                onClick={() => { setScheduleError(''); setScheduleEditor({ lightId: light.id, profile }); }}>{t('Edit schedule')}</Button>}
                        </Stack>
                        {profile === 'night' && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t('Night automatically covers all time outside Normal and scheduled presets.')}</Typography>}
                        <ScheduleSummary schedule={light.schedule?.[profile] || []} emptyLabel={t('No active intervals.')} />
                    </Box>)}
                </Stack>
                <TrafficLightPresets light={light} endpoints={endpoints} runtime={runtime}
                    onUseSchedule={() => setPresetRequest({ lightId: light.id, kind: 'schedule', key: Date.now() })}
                    onSave={async (presets) => {
                        const { data } = await request(endpoints.update.replace('__ID__', light.id), 'PUT', { presets });
                        updateLight(data);
                    }}
                    onApply={(preset, seconds) => setPresetRequest({ lightId: light.id, preset, seconds, key: Date.now() })} />
            </CardContent></Card></Grid>)}
        </Grid>}
        <Dialog open={Boolean(form)} onClose={() => !saving && setForm(null)} maxWidth="md" fullWidth>
            <DialogTitle>{t(form?.id ? 'Edit traffic light' : 'Add traffic light')}</DialogTitle>
            <DialogContent><Stack spacing={3} sx={{ pt: 1 }}>
                {error && <Alert severity="error">{error}</Alert>}
                <TextField autoFocus required label={t('Name')} value={form?.name || ''} disabled={saving} inputProps={{ maxLength: 255 }} onChange={(event) => setForm({ ...form, name: event.target.value })} />
                <TextField label={t('Description')} multiline value={form?.description || ''} disabled={saving} inputProps={{ maxLength: 5000 }} onChange={(event) => setForm({ ...form, description: event.target.value })} />

            </Stack></DialogContent>
            <DialogActions><Button disabled={saving} onClick={() => setForm(null)}>{t('Cancel')}</Button><Button variant="contained" disabled={saving || !form?.name.trim()} onClick={save}>{t(saving ? 'Saving...' : 'Save')}</Button></DialogActions>
        </Dialog>
        <ScheduleDrawer open={Boolean(scheduleEditor)} onClose={() => !scheduleSaving && setScheduleEditor(null)}
            title={`${t('Schedule')} · ${t(labels[scheduleEditor?.profile] || 'Normal')} · ${scheduleLight?.name || ''}`}
            initialSchedule={scheduleLight?.base_schedule || scheduleLight?.schedule?.normal || EMPTY_SCHEDULE}
            headerContent={<Stack spacing={1}>
                <Alert severity="info">{t('Edit the Normal base schedule. Scheduled presets take over their intervals; flashing amber covers the remaining time.')}</Alert>
                {scheduleError && <Alert severity="error">{scheduleError}</Alert>}
            </Stack>}
            emptyScheduleLabel={t('No Normal intervals. Scheduled presets still run; flashing amber fills the rest.')}
            disabled={scheduleSaving} onSave={saveSchedule} />
        <Dialog open={Boolean(deviceNavigation)} onClose={() => setDeviceNavigation(null)} maxWidth="xs" fullWidth>
            <DialogTitle>{t('Open device settings')}</DialogTitle>
            <DialogContent><Typography>{t('Relay configuration opens in Device Management in a new tab.')} {deviceNavigation?.device_name}</Typography></DialogContent>
            <DialogActions>
                <Button onClick={() => setDeviceNavigation(null)}>{t('Cancel')}</Button>
                <Button component="a" href={deviceNavigation?.device_edit_url} target="_blank" rel="noopener noreferrer"
                    variant="contained" endIcon={<OpenInNewIcon />} onClick={() => setDeviceNavigation(null)}>{t('Open device')}</Button>
            </DialogActions>
        </Dialog>
        <Dialog open={Boolean(deleting)} onClose={() => !saving && setDeleting(null)}>
            <DialogTitle>{t('Delete traffic light')}</DialogTitle><DialogContent>
                {error && <Alert severity="error">{error}</Alert>}<Typography>{t('Delete this traffic light?')} {deleting?.name}</Typography>
            </DialogContent><DialogActions><Button disabled={saving} onClick={() => setDeleting(null)}>{t('Cancel')}</Button><Button color="error" disabled={saving} onClick={remove}>{t('Delete')}</Button></DialogActions>
        </Dialog>
    </Stack>;
}
